import { ActionIcon, Box, Flex, Tabs } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import React, { useEffect, useRef, useState } from "react";
import "./ScrollableTabs.scss";

interface ScrollableTabsProps {
  tabLabels: string[];
  tabIcons?: Record<string, React.ReactNode>;
  activeTabIndex: number;
  onTabClick: (index: number) => void;
}

const ScrollableTabs: React.FC<ScrollableTabsProps> = ({
  tabLabels,
  tabIcons,
  activeTabIndex,
  onTabClick,
}) => {
  const tabsListRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScrollPosition = () => {
    const container = tabsListRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
  };

  const scroll = (direction: "left" | "right") => {
    const container = tabsListRef.current;
    if (!container) return;

    const scrollAmount = 300; // Roughly 2-3 tabs
    const targetScroll =
      direction === "left"
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: targetScroll,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    checkScrollPosition();
    const container = tabsListRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(checkScrollPosition);
    resizeObserver.observe(container);

    container.addEventListener("scroll", checkScrollPosition);

    return () => {
      resizeObserver.disconnect();
      container.removeEventListener("scroll", checkScrollPosition);
    };
  }, [tabLabels]);

  return (
    <Box className="scrollable-tabs-wrapper">
      {showLeftArrow && (
        <Box className="scroll-button-slot">
          <ActionIcon
            className="scroll-button scroll-button-left"
            onClick={() => scroll("left")}
            variant="subtle"
            size="lg"
            color="gray"
          >
            <IconChevronLeft size={20} />
          </ActionIcon>
        </Box>
      )}

      <Box className="scrollable-tabs-container">
        <Tabs.List ref={tabsListRef} className="scrollable-tabs-list">
          {tabLabels.map((label, index) => (
            <Tabs.Tab
              onClick={() => onTabClick(index)}
              className="tab"
              data-intercom-target={label.toLowerCase()}
              key={`${label}-${index}`}
              value={label}
            >
              <Flex align="center" gap={6} style={{ height: "100%" }}>
                {tabIcons?.[label] && (
                  <Box
                    style={{
                      color: activeTabIndex === index ? "#07c" : "#4d5357",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {tabIcons[label]}
                  </Box>
                )}
                <Box
                  component="span"
                  style={{
                    color: activeTabIndex === index ? "#07c" : "#4d5357",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {label}
                </Box>
              </Flex>
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Box>

      {showRightArrow && (
        <Box className="scroll-button-slot">
          <ActionIcon
            className="scroll-button scroll-button-right"
            onClick={() => scroll("right")}
            variant="subtle"
            size="lg"
            color="gray"
          >
            <IconChevronRight size={20} />
          </ActionIcon>
        </Box>
      )}
    </Box>
  );
};

export default ScrollableTabs;
